import React, { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import fastDeepEqual from 'fast-deep-equal'

import { ConditionValue } from '@makyoapp/makyo-common'
import {
  cn,
  ColorVariant,
  getHoverClass,
  getHoverVars,
  HoverEffectVariant,
  MaterialIcon,
  useMtwContext,
} from '@makyoapp/makyo-tw-ui'

import { IViewComponent } from '@/core/app-builder/app/component'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { ComponentSettingKey } from '@/shared/builder'

import { BackgroundLayer } from './layers'

const VERTICAL_ALIGN_MAP = {
  center: 'center',
  start: 'flex-start',
  end: 'flex-end',
}

const HORIZONTAL_ALIGN_MAP = {
  center: 'center',
  start: 'flex-start',
  end: 'flex-end',
}

type ColumnLayout = number[]

const COLUMN_LAYOUTS: ColumnLayout[] = [
  [12],
  [6, 6],
  [7.2, 4.8],
  [4.8, 7.2],
  [8.4, 3.6],
  [3.6, 8.4],
  [8, 4],
  [4, 8],
  [3, 9],
  [9, 3],
  [4, 4, 4],
  [3, 3, 3, 3],
]

// ======================================================================
// CompSection
// ======================================================================

export interface CompSectionSettings {
  hoverEffect?: HoverEffectVariant
  currentParentBg: ColorVariant | undefined
  style?: React.CSSProperties
  bgLayerStyle?: Record<string, any>
  withOverlay: boolean
  overlayColor: string
  opacity: number
  verticalAlign: string
  horizontalAlign: string
  itemGap: string
  sameHeight: boolean
  backgroundSettings: any[]
  videoSrc?: string
  imageSrc?: string
  bgStyles: Record<string, any>
  layout?: number[]
  orientation?: string
  borderProps?: Record<string, string>
  reverseOrder?: boolean
  variant?: string
  elevation?: boolean
  stickyPosition?: 'top' | 'bottom'
  stickyOffset?: number
  hideOnScroll?: boolean
  hideOnScrollThreshold?: number
  component: IViewComponent
  widthMode?: 'auto' | 'custom' | 'full-viewport'
  heightMode?: 'auto' | 'custom' | 'full-viewport'
  height?: number | string
}

export interface BaseCompSectionProps {
  sectionSettings: CompSectionSettings
  baseProps: React.HTMLAttributes<HTMLElement>
}

export interface CompSectionProps extends BaseCompSectionProps {
  onCheckVisibility: (visibility: ConditionValue) => boolean
  children: React.ReactNode
}

/**
 * CompSectionCore
 */
export function CompSectionCore({ sectionSettings, baseProps, children, onCheckVisibility }: CompSectionProps) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  const [isHidden, setIsHidden] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const { globalDoc } = useMtwContext()

  // ========================= refs =========================
  const containerRef = useRef<HTMLDivElement>(null)

  // ========================= derived =========================
  const {
    style,
    itemGap,
    verticalAlign,
    backgroundSettings,
    videoSrc,
    imageSrc,
    bgStyles,
    layout,
    horizontalAlign,
    orientation,
    borderProps,
    reverseOrder,
    variant,
    elevation,
    stickyPosition = 'top',
    stickyOffset = 0,
    hideOnScroll = true,
    hideOnScrollThreshold = 10,
    component,
    hoverEffect,
    currentParentBg,
    sameHeight,
    heightMode = 'auto',
    height = 'auto',
  } = sectionSettings

  const vAlign = VERTICAL_ALIGN_MAP[verticalAlign as keyof typeof VERTICAL_ALIGN_MAP]
  const hAlign = HORIZONTAL_ALIGN_MAP[horizontalAlign as keyof typeof HORIZONTAL_ALIGN_MAP]
  const isFullWidth = fastDeepEqual(layout, COLUMN_LAYOUTS[0])
  const isSticky = variant === 'sticky'

  const getCalculatedHeight = useCallback(() => {
    if (heightMode === 'full-viewport') {
      return '100vh'
    } else if (heightMode === 'custom') {
      const heightValue = typeof height === 'number' ? height : parseInt(String(height), 10)
      const heightUnit =
        typeof height === 'string' && height.includes('%')
          ? '%'
          : typeof height === 'string' && height.includes('vh')
            ? 'vh'
            : 'px'
      return isNaN(heightValue) ? 'auto' : `${heightValue}${heightUnit}`
    }
    return 'auto'
  }, [heightMode, height])

  // ========================= scroll detection =========================

  const lastScrollYRef = useRef(0)

  useEffect(() => {
    if (!hideOnScroll || !isSticky || !globalDoc) return
    const scrollTarget = globalDoc.defaultView || window

    const handleScroll = () => {
      try {
        const currentScrollY = scrollTarget.scrollY || scrollTarget.pageYOffset || 0
        const scrollDelta = currentScrollY - lastScrollYRef.current

        if (scrollDelta > hideOnScrollThreshold) {
          setIsHidden(true)
        } else if (scrollDelta < -hideOnScrollThreshold || currentScrollY < 100) {
          setIsHidden(false)
        }

        lastScrollYRef.current = currentScrollY
      } catch (error) {
        console.error('Error in scroll handler:', error)
      }
    }

    scrollTarget.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll)
    }
  }, [hideOnScroll, isSticky, hideOnScrollThreshold, globalDoc])

  // ========================= utils =========================

  const getElevationStyles = useCallback((elevation: boolean | number) => {
    if (!elevation) return {}

    // If elevation is boolean true, use default elevation (level 2)
    const level = typeof elevation === 'number' ? elevation : 2

    const elevationMap = {
      1: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
      },
      2: {
        boxShadow: '0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)',
      },
      3: {
        boxShadow: '0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)',
      },
      4: {
        boxShadow: '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
      },
      5: {
        boxShadow: '0 19px 38px rgba(0, 0, 0, 0.30), 0 15px 12px rgba(0, 0, 0, 0.22)',
      },
    }

    return elevationMap[level as keyof typeof elevationMap] || elevationMap[2]
  }, [])

  const getGridTemplateColumns = useCallback((layout: ColumnLayout, itemGap: number = 16) => {
    const totalGapSpace = (layout.length - 1) * itemGap
    return layout
      .map((col) => {
        const percentage = (col / 12) * 100
        return `calc(${percentage}% - ${totalGapSpace / layout.length}px)`
      })
      .join(' ')
  }, [])

  const getOrientationStyle = useCallback(() => {
    if (orientation === 'horizontal') {
      // mode = horizontal && full width
      if (isFullWidth) {
        return {
          display: 'flex',
          flexDirection: reverseOrder ? 'row-reverse' : 'row',
          justifyContent: hAlign,
          alignItems: vAlign,
        }
      }

      // mode = horizontal && using layout columns (50/50, 70/30, etc.)
      // Note: Alignment is handled per-item via wrapper divs in renderChildren
      // We don't set justifyItems/alignItems here to allow per-item control
      return {
        display: 'grid',
        gridTemplateColumns: getGridTemplateColumns(layout ?? [12], parseInt(itemGap)),
      }
    }

    // mode = vertical
    return {
      display: 'flex',
      flexDirection: reverseOrder ? 'column-reverse' : 'column',
      justifyContent: vAlign,
      alignItems: hAlign,
    }
  }, [orientation, reverseOrder, vAlign, hAlign, isFullWidth, getGridTemplateColumns, layout, itemGap])

  // ========================= sticky styles =========================

  const getStickyStyles = useCallback(() => {
    if (!isSticky)
      return {
        position: 'relative',
      }

    const baseStickyStyles = {
      position: 'fixed' as const,
      left: 0,
      right: 0,
      zIndex: 1000,
      width: '100%',
      minHeight: 'auto',
      transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
    }

    // For sticky top, use sticky with top: 0
    if (stickyPosition === 'top') {
      return {
        ...baseStickyStyles,
        top: stickyOffset,
        transform: isHidden ? 'translateY(-100%)' : 'translateY(0)',
        opacity: isHidden ? 0 : 1,
      }
    }

    // For sticky bottom, we need to use a different approach
    // Use fixed positioning with bottom offset for true bottom sticky behavior
    return {
      ...baseStickyStyles,
      bottom: stickyOffset,
      transform: isHidden ? 'translateY(100%)' : 'translateY(0)',
      opacity: isHidden ? 0 : 1,
    }
  }, [isSticky, stickyOffset, stickyPosition, isHidden])

  // ========================= render =========================

  const renderChildren = useCallback(() => {
    const validChildren = React.Children.toArray(children)
      .filter(React.isValidElement)
      .map((child: React.ReactElement) => {
        if (!child || !child.props) return null

        const elId = child.props.element?.id
        if (!elId) return null

        const childSettings = child.props.containerClient?.settings?._BodySettings?.[elId] || {}
        const visibility = childSettings['behavior-visibility'] as ConditionValue

        let isVisible = true
        if (visibility) {
          isVisible = onCheckVisibility(visibility)
        }
        if (!isVisible) return null

        // Get item-specific alignment settings (for column items in grid layout)
        const itemHAlign = childSettings[ComponentSettingKey.ColumnItemDesignHAlign] as string | undefined
        const itemVAlign = childSettings[ComponentSettingKey.ColumnItemDesignVAlign] as string | undefined

        // For grid layout (horizontal with columns, not full width), wrap all items with alignment wrapper
        // This allows per-item alignment control
        const isGridLayout = orientation === 'horizontal' && !isFullWidth

        if (isGridLayout) {
          // Use item-specific alignment if available, otherwise use section default
          const itemHAlignValue = itemHAlign
            ? HORIZONTAL_ALIGN_MAP[itemHAlign as keyof typeof HORIZONTAL_ALIGN_MAP] || hAlign
            : hAlign
          const itemVAlignValue = itemVAlign
            ? VERTICAL_ALIGN_MAP[itemVAlign as keyof typeof VERTICAL_ALIGN_MAP] || vAlign
            : vAlign

          // Wrap child with alignment wrapper div
          return (
            <div
              key={elId}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: itemVAlignValue,
                alignItems: itemHAlignValue,
                width: '100%',
                height: sameHeight ? '100%' : 'auto',
              }}
            >
              {React.cloneElement(child, child.props)}
            </div>
          )
        }

        return React.cloneElement(child, child.props)
      })
      .filter(Boolean)

    // Apply reverse order for grid horizontal (non-fullwidth)
    if (orientation === 'horizontal' && !isFullWidth && reverseOrder) {
      return validChildren.reverse()
    }

    return validChildren
  }, [children, onCheckVisibility, orientation, isFullWidth, reverseOrder, hAlign, vAlign, sameHeight])

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement

    if (target === e.currentTarget) {
      if (baseProps.id) {
        openComponentSettings(component)
      }
      if (isDevelopmentMode) return
      return
    }

    const isBackgroundElement =
      (target.classList.contains('absolute') && target.classList.contains('inset-0')) ||
      target.closest('.absolute.inset-0') !== null

    const clickedComponent = target.closest('[data-component-type]')
    const isChildComponent =
      clickedComponent &&
      clickedComponent !== e.currentTarget &&
      clickedComponent.getAttribute('data-component-type') !== 'section'

    if (isBackgroundElement && !isChildComponent) {
      if (baseProps.id) {
        openComponentSettings(component)
      }
      if (isDevelopmentMode) return
      return
    }

    if (isChildComponent) {
      return
    }

    const computedStyle = window.getComputedStyle(target)
    const isPointerEventsNone = computedStyle.pointerEvents === 'none'

    if (isPointerEventsNone && !isChildComponent) {
      if (baseProps.id) {
        openComponentSettings(component)
      }
      if (isDevelopmentMode) return
    }
  }

  // Extract common section structure
  const renderSectionContent = () => (
    <>
      <div
        className='absolute inset-0'
        style={{
          overflow: 'hidden',
          borderRadius: 'inherit',
          zIndex: -1,
          ...borderProps,
        }}
      >
        {bgStyles?.map((layerStyle: any, index: number) => (
          <BackgroundLayer
            key={index}
            layerStyle={layerStyle}
            index={index}
            backgroundSettings={backgroundSettings}
            videoSrc={videoSrc}
            containerRef={containerRef}
          />
        ))}
      </div>
      {renderChildren()}
    </>
  )

  const getPaddingTopNumber = (style: React.CSSProperties | undefined): number => {
    if (!style?.padding) return 0
    const padding = style.padding
    if (typeof padding === 'string') {
      const parts = padding.trim().split(/\s+/)
      const topPadding = parts[0]
      if (topPadding) {
        const number = parseFloat(topPadding.replace(/[^\d.-]/g, ''))
        return isNaN(number) ? 0 : number
      }
    }
    if (typeof padding === 'number') return padding
    return 0
  }

  const paddingTopNumber = getPaddingTopNumber(baseProps.style)

  const hoverIntensity = 'md'

  const hoverClass = getHoverClass(hoverEffect, hoverIntensity, 'container')
  const hoverVars = getHoverVars((hoverEffect as HoverEffectVariant) || 'none', {
    colorVariantForContainer: currentParentBg as ColorVariant,
  })

  // Single section element with conditional wrapper
  const sectionElement = (
    <section
      {...baseProps}
      data-component-type='section'
      ref={containerRef}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={clsx(
        baseProps.className,
        'mk-comp-section relative',
        isSticky && 'mk-comp-section-sticky',
        isHidden && 'mk-comp-section-hidden',
        hoverClass
      )}
      style={
        {
          ...baseProps.style,
          ...style,
          ...hoverVars,
          zIndex: isSticky ? 1000 : 0,
          maxWidth: '100%',
          minHeight: heightMode === 'full-viewport' ? '100vh' : '100%',
          height: getCalculatedHeight(),
          overflow: 'hidden',
          gap: itemGap,
          ...getStickyStyles(),
          ...getOrientationStyle(),
          ...getElevationStyles(elevation ?? false),
          ...(isDevelopmentMode &&
            isHovered && {
              border: '1px dashed #333',
              cursor: 'pointer',
            }),
        } as React.CSSProperties
      }
    >
      {renderSectionContent()}
    </section>
  )

  return sectionElement
}
